import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  FileText,
  Download,
  DollarSign,
  Users,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Loader2,
  Upload,
  Send,
  Search,
  UserPlus,
  TrendingUp,
  ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgentSubscriptions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [commissions, setCommissions] = useState(null);
  const [showAddBuilding, setShowAddBuilding] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('buildings');
  const [newBuilding, setNewBuilding] = useState({
    name: '',
    address: '',
    city: '',
    unit_count: '',
    contact_name: '',
    contact_email: '',
    contact_phone: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get company info
      const companyRes = await fetch(`${API_URL}/api/agent-subscriptions/my-company`, {
        credentials: 'include'
      });
      if (companyRes.ok) {
        const companyData = await companyRes.json();
        setCompany(companyData);
      } else if (companyRes.status === 404) {
        setError('No company associated with your account. Please contact support.');
        setLoading(false);
        return;
      } else {
        const err = await companyRes.json();
        setError(err.detail || 'Failed to load company data');
        setLoading(false);
        return;
      }

      // Get buildings
      const buildingsRes = await fetch(`${API_URL}/api/agent-subscriptions/buildings`, {
        credentials: 'include'
      });
      if (buildingsRes.ok) {
        setBuildings(await buildingsRes.json());
      }

      // Get invoices
      const invoicesRes = await fetch(`${API_URL}/api/agent-subscriptions/invoices`, {
        credentials: 'include'
      });
      if (invoicesRes.ok) {
        setInvoices(await invoicesRes.json());
      }

      // Get commissions
      const commissionsRes = await fetch(`${API_URL}/api/agent-subscriptions/commissions/summary`, {
        credentials: 'include'
      });
      if (commissionsRes.ok) {
        setCommissions(await commissionsRes.json());
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBuilding = async () => {
    if (!newBuilding.name || !newBuilding.address || !newBuilding.city || !newBuilding.unit_count) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/agent-subscriptions/buildings/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newBuilding,
          unit_count: parseInt(newBuilding.unit_count)
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Building added! Payment due in ${data.grace_period_days} days.`);
        setShowAddBuilding(false);
        setNewBuilding({ name: '', address: '', city: '', unit_count: '', contact_name: '', contact_email: '', contact_phone: '' });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add building');
      }
    } catch (error) {
      toast.error('Failed to add building');
    } finally {
      setSubmitting(false);
    }
  };

  const handleIssueInvoice = async (buildingId) => {
    try {
      const res = await fetch(`${API_URL}/api/agent-subscriptions/buildings/${buildingId}/issue-invoice`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Invoice ${data.invoice_number} issued!`);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to issue invoice');
      }
    } catch (error) {
      toast.error('Failed to issue invoice');
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/agent-subscriptions/invoices/${invoiceId}/pdf`, {
        credentials: 'include'
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice_${invoiceId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Failed to download invoice');
      }
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending Payment</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3 mr-1" />Overdue</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredBuildings = buildings.filter(b => 
    b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-lg mx-auto mt-20">
          <Card>
            <CardContent className="pt-8 pb-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Unable to Load Subscriptions</h2>
              <p className="text-slate-600 mb-6">{error}</p>
              <div className="space-y-3">
                <Button onClick={fetchData} className="w-full">
                  Try Again
                </Button>
                <Button variant="outline" onClick={() => navigate('/agent')} className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/agent')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Subscription Management</h1>
              <p className="text-slate-500">
                {company?.name || company?.company_name} {company?.company_code && <>• Code: <span className="font-mono font-semibold">{company?.company_code}</span></>}
              </p>
            </div>
          </div>
          <Button onClick={() => setShowAddBuilding(true)} data-testid="add-building-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Building
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Buildings</p>
                  <p className="text-2xl font-bold">{company?.building_stats?.total || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Active</p>
                  <p className="text-2xl font-bold">{company?.building_stats?.active || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pending Payment</p>
                  <p className="text-2xl font-bold">{company?.building_stats?.pending || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Commission Earned</p>
                  <p className="text-2xl font-bold">R{(commissions?.pending_commission || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'buildings' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-500'}`}
            onClick={() => setActiveTab('buildings')}
          >
            Managed Buildings
          </button>
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'invoices' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-500'}`}
            onClick={() => setActiveTab('invoices')}
          >
            Invoices
          </button>
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'commissions' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-500'}`}
            onClick={() => setActiveTab('commissions')}
          >
            Commissions
          </button>
        </div>

        {/* Buildings Tab */}
        {activeTab === 'buildings' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Managed Buildings</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search buildings..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Building</TableHead>
                    <TableHead>Units</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Annual Fee</TableHead>
                    <TableHead>Payment Due</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBuildings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No buildings found. Click "Add Building" to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBuildings.map((building) => (
                      <TableRow key={building.building_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{building.name}</p>
                            <p className="text-sm text-slate-500">{building.address}, {building.city}</p>
                          </div>
                        </TableCell>
                        <TableCell>{building.unit_count}</TableCell>
                        <TableCell>{getStatusBadge(building.subscription_status)}</TableCell>
                        <TableCell>R{building.annual_subscription?.toLocaleString()}</TableCell>
                        <TableCell>
                          {building.subscription_status === 'pending' && building.days_until_payment_due !== undefined ? (
                            <span className={building.days_until_payment_due <= 14 ? 'text-amber-600 font-medium' : ''}>
                              {building.days_until_payment_due} days
                            </span>
                          ) : building.renewal_date ? (
                            new Date(building.renewal_date).toLocaleDateString()
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {building.subscription_status !== 'active' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleIssueInvoice(building.building_id)}
                                data-testid={`issue-invoice-${building.building_id}`}
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Issue Invoice
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <Card>
            <CardHeader>
              <CardTitle>Invoices</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Building</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Issue Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No invoices yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.map((invoice) => (
                      <TableRow key={invoice.invoice_id}>
                        <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                        <TableCell>{invoice.building_name}</TableCell>
                        <TableCell>R{invoice.amount?.toLocaleString()}</TableCell>
                        <TableCell>
                          {invoice.status === 'paid' ? (
                            <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>
                          ) : invoice.status === 'overdue' ? (
                            <Badge className="bg-red-100 text-red-700">Overdue</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell>{new Date(invoice.issue_date).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(invoice.due_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDownloadInvoice(invoice.invoice_id)}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Commissions Tab */}
        {activeTab === 'commissions' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Commission Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Total Earned</p>
                    <p className="text-2xl font-bold text-slate-900">
                      R{(commissions?.company?.total_commission_earned || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <p className="text-sm text-emerald-600">Paid Out</p>
                    <p className="text-2xl font-bold text-emerald-700">
                      R{(commissions?.company?.total_commission_paid || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4">
                    <p className="text-sm text-amber-600">Pending</p>
                    <p className="text-2xl font-bold text-amber-700">
                      R{(commissions?.pending_commission || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  <p className="text-sm text-slate-500 mb-2">Commission Rate: {company?.commission_rate || 10}%</p>
                  <p className="text-sm text-slate-500">Commissions are paid quarterly (March, June, September, December)</p>
                </div>
              </CardContent>
            </Card>

            {commissions?.commissions?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Commission History</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Building</TableHead>
                        <TableHead>Subscription</TableHead>
                        <TableHead>Commission</TableHead>
                        <TableHead>Quarter</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {commissions.commissions.map((comm) => (
                        <TableRow key={comm.commission_id}>
                          <TableCell>{comm.building_name}</TableCell>
                          <TableCell>R{comm.subscription_amount?.toLocaleString()}</TableCell>
                          <TableCell className="font-medium">R{comm.commission_amount?.toLocaleString()}</TableCell>
                          <TableCell>{comm.quarter}</TableCell>
                          <TableCell>
                            {comm.status === 'paid' ? (
                              <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Add Building Dialog */}
        <Dialog open={showAddBuilding} onOpenChange={setShowAddBuilding}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Building</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Building Name *</Label>
                <Input
                  value={newBuilding.name}
                  onChange={(e) => setNewBuilding({ ...newBuilding, name: e.target.value })}
                  placeholder="Sunset Heights Estate"
                  data-testid="building-name-input"
                />
              </div>
              <div>
                <Label>Address *</Label>
                <Input
                  value={newBuilding.address}
                  onChange={(e) => setNewBuilding({ ...newBuilding, address: e.target.value })}
                  placeholder="123 Main Street"
                />
              </div>
              <div>
                <Label>City *</Label>
                <Input
                  value={newBuilding.city}
                  onChange={(e) => setNewBuilding({ ...newBuilding, city: e.target.value })}
                  placeholder="Cape Town"
                />
              </div>
              <div>
                <Label>Number of Units *</Label>
                <Input
                  type="number"
                  value={newBuilding.unit_count}
                  onChange={(e) => setNewBuilding({ ...newBuilding, unit_count: e.target.value })}
                  placeholder="50"
                  data-testid="unit-count-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={newBuilding.contact_name}
                    onChange={(e) => setNewBuilding({ ...newBuilding, contact_name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={newBuilding.contact_email}
                    onChange={(e) => setNewBuilding({ ...newBuilding, contact_email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              
              {newBuilding.unit_count && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600">Subscription Summary</p>
                  <p className="text-lg font-bold text-blue-700">
                    R{Math.max(35 * parseInt(newBuilding.unit_count || 0), 750) * 12}/year
                  </p>
                  <p className="text-xs text-blue-500">R35 per unit/month (min R750/month)</p>
                  <p className="text-xs text-blue-500 mt-1">60-day payment window after activation</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddBuilding(false)}>Cancel</Button>
              <Button onClick={handleAddBuilding} disabled={submitting} data-testid="save-building-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Add Building
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
